import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { LoadingButtonContent } from "../ui/loading-button-content";
import { useToast } from "@/hooks/use-toast";
import { useShopStore } from "@/stores/shopStore";
import { useImpersonationStore } from "@/stores/impersonationStore";
import { supabase } from "@/lib/supabaseClient";

export function ImpersonationForm() {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const { resetShop } = useShopStore();
    const { toast } = useToast();
    const { setImpersonationEmail } = useImpersonationStore();

    const handleImpersonation = async () => {
        if (!email || !password) {
            toast({
                title: "Missing information",
                description: "Please enter both email and admin password",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        resetShop();

        try {
            const response = await fetch(
                `${
                    import.meta.env.VITE_OPTIFI_API_URL
                }/admin/start_user_session`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${password}`,
                    },
                    body: JSON.stringify({ email }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.access_token && data.refresh_token) {
                // Set the session using Supabase
                const { error } = await supabase.auth.setSession({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token,
                });

                if (error) {
                    throw error;
                }

                toast({
                    title: "Impersonation successful",
                    description: `Now logged in as ${email}`,
                });

                setImpersonationEmail(email);
            } else {
                throw new Error("Invalid response format");
            }
        } catch (error) {
            console.error("Impersonation error:", error);
            toast({
                title: "Impersonation failed",
                description:
                    error instanceof Error
                        ? error.message
                        : "An unknown error occurred",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="mx-auto max-w-sm">
            <CardHeader>
                <div className="flex justify-center mb-4">
                    <img
                        src="/optifi_logo.png"
                        alt="Logo"
                        className="h-12 w-auto"
                    />
                </div>
                <CardTitle className="text-2xl">Admin Impersonation</CardTitle>
                <CardDescription>
                    Enter user email and admin password to impersonate a user
                    for support
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid gap-4">
                    <div className="grid gap-2">
                        <Label htmlFor="email">User Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="user@example.com"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="password">Admin Password</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="Enter admin password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    <Button
                        onClick={handleImpersonation}
                        className="w-full"
                        disabled={loading}
                    >
                        <LoadingButtonContent loading={loading}>
                            Start Impersonation
                        </LoadingButtonContent>
                    </Button>
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                    This feature is for support and debugging purposes only
                </div>
            </CardContent>
        </Card>
    );
}
